import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation, Link } from "react-router-dom";
import {
  X,
  Printer,
  Edit,
  Check,
  Send,
  PackageCheck,
  Trash2,
  Loader,
  Upload,
  ArrowRight,
  CheckCircle2,
  Eye,
  Image as ImageIcon,
} from "lucide-react";
import Sidebar from "../components/Sidebar";
import Header from "../components/Header";
import ConfirmationModal from "../components/ConfirmationModal";
import Modal from "../components/Modal";
import AddProcurementModal from "../components/AddProcurementModal";
import {
  formatNumberWithSeparator,
  parseNumberFromSeparator,
} from "../utils/numberFormat";
import * as inventoryService from "../api/inventoryService";
import { masterService } from "../api/masterService";
import { useNotification } from "../contexts/NotificationContext";

export default function InventoryDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { showNotification } = useNotification();

  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => {
    const saved = localStorage.getItem("sidebarCollapsed");
    return saved !== null ? JSON.parse(saved) : true;
  });

  const isRetail = false;

  // Data State
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [items, setItems] = useState([]);
  const [receivings, setReceivings] = useState([]);
  const [status, setStatus] = useState("");
  const [processing, setProcessing] = useState(false);
  const [previewImageUrl, setPreviewImageUrl] = useState(null);

  // Edit PO Modal
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  // Print Goods Receipt Modal State
  const [selectedRcvToPrint, setSelectedRcvToPrint] = useState(null);

  // Receiving Intake Modal State
  const [isReceiveModalOpen, setIsReceiveModalOpen] = useState(false);
  const [receivedDate, setReceivedDate] = useState(() => new Date().toISOString().split("T")[0]);
  const [receivedQuantities, setReceivedQuantities] = useState({});
  const [actualPrices, setActualPrices] = useState({});
  const [itemConditions, setItemConditions] = useState({});
  const [batchNumbers, setBatchNumbers] = useState({});
  const [receivingNotes, setReceivingNotes] = useState("");
  const [attachments, setAttachments] = useState([]);

  // Confirmation Modal
  const [confirmConfig, setConfirmConfig] = useState({
    isOpen: false,
    title: "",
    message: "",
    confirmText: "",
    type: "info",
    onConfirm: null,
  });

  useEffect(() => {
    fetchData();
  }, [id]);

  // Auto open receive modal if navigated with state autoReceive: true
  useEffect(() => {
    if (location.state?.autoReceive && data && (status === "ORDERED" || status === "PARTIAL_RECEIVED")) {
      setIsReceiveModalOpen(true);
    }
  }, [location.state, data, status]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await inventoryService.getPurchaseOrderDetail(id);
      const detailData = response?.data || response;

      setData(detailData);
      setItems(detailData?.items || []);
      setStatus(detailData?.status || "");

      // Fetch Receivings for this PO
      try {
        const rcvRes = await inventoryService.getReceivingsByPO(id);
        const rcvList = rcvRes?.data || (Array.isArray(rcvRes) ? rcvRes : []);
        setReceivings(rcvList);
      } catch (errRcv) {
        console.error("Error fetching receivings for PO:", errRcv);
      }

      // Initialize receiving quantities and prices
      const initialQtys = {};
      const initialPrices = {};
      (detailData?.items || []).forEach((item) => {
        const totalOrdered = Number(item.qty_ordered || item.quantity || 0);
        const alreadyReceived = Number(item.qty_received || 0);
        const remaining = Math.max(0, totalOrdered - alreadyReceived);
        initialQtys[item.id] = remaining > 0 ? remaining : totalOrdered;
        initialPrices[item.id] =
          item.unit_price_realized ||
          item.unit_price_actual ||
          item.unit_price_estimated ||
          item.unit_price ||
          0;
      });
      setReceivedQuantities(initialQtys);
      setActualPrices(initialPrices);
    } catch (err) {
      console.error("Error fetching PO detail:", err);
      showNotification("error", "Gagal memuat detail Purchase Order");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (val) => {
    if (!val) return "-";
    try {
      return new Date(val).toLocaleDateString("id-ID", {
        day: "numeric",
        month: "short",
        year: "numeric",
      });
    } catch {
      return val;
    }
  };

  const formatCurrency = (val) => {
    if (val === undefined || val === null || isNaN(val)) return "Rp 0";
    return new Intl.NumberFormat("id-ID", {
      style: "currency",
      currency: "IDR",
      minimumFractionDigits: 0,
    }).format(val);
  };

  const getStatusBadge = (s) => {
    switch (s?.toUpperCase()) {
      case "DRAFT":
        return (
          <span className="px-3 py-1 rounded-full text-xs font-semibold bg-slate-100 text-slate-700 ring-1 ring-slate-200">
            Draft
          </span>
        );
      case "SUBMITTED":
        return (
          <span className="px-3 py-1 rounded-full text-xs font-semibold bg-amber-50 text-amber-700 ring-1 ring-amber-200">
            Menunggu Persetujuan
          </span>
        );
      case "APPROVED":
        return (
          <span className="px-3 py-1 rounded-full text-xs font-semibold bg-sky-50 text-sky-700 ring-1 ring-sky-200">
            Disetujui
          </span>
        );
      case "ORDERED":
        return (
          <span className="px-3 py-1 rounded-full text-xs font-semibold bg-indigo-50 text-indigo-700 ring-1 ring-indigo-200">
            Sedang Dipesan
          </span>
        );
      case "PARTIAL_RECEIVED":
        return (
          <span className="px-3 py-1 rounded-full text-xs font-semibold bg-amber-50 text-amber-700 ring-1 ring-amber-200">
            Diterima Sebagian
          </span>
        );
      case "COMPLETED":
        return (
          <span className="px-3 py-1 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200">
            Selesai Diterima
          </span>
        );
      case "CANCELLED":
        return (
          <span className="px-3 py-1 rounded-full text-xs font-semibold bg-rose-50 text-rose-700 ring-1 ring-rose-300">
            Dibatalkan
          </span>
        );
      case "REJECTED":
        return (
          <span className="px-3 py-1 rounded-full text-xs font-semibold bg-rose-50 text-rose-700 ring-1 ring-rose-300">
            Ditolak
          </span>
        );
      default:
        return (
          <span className="px-3 py-1 rounded-full text-xs font-semibold bg-slate-100 text-slate-700 ring-1 ring-slate-200">
            {s || "-"}
          </span>
        );
    }
  };

  const handleUpdateStatus = async (nextStatus) => {
    try {
      setProcessing(true);
      await inventoryService.updatePurchaseOrderStatus(id, {
        status: nextStatus,
      });
      showNotification("success", `Status Purchase Order berhasil diubah`);
      setConfirmConfig({ ...confirmConfig, isOpen: false });
      await fetchData();
    } catch (err) {
      console.error("Failed to update status:", err);
      showNotification(
        "error",
        err.response?.data?.message || "Gagal mengubah status Purchase Order",
      );
    } finally {
      setProcessing(false);
    }
  };

  const handleConfirmCancel = () => {
    setConfirmConfig({
      isOpen: true,
      title: "Batalkan Purchase Order?",
      message: "Apakah Anda yakin ingin membatalkan Purchase Order ini?",
      confirmText: "Batalkan PO",
      type: "danger",
      onConfirm: () => handleUpdateStatus("CANCELLED"),
    });
  };

  const handleConfirmApprove = () => {
    setConfirmConfig({
      isOpen: true,
      title: "Setujui Purchase Order?",
      message: "Apakah Anda yakin ingin menyetujui Purchase Order ini?",
      confirmText: "Setujui PO",
      type: "info",
      onConfirm: () => handleUpdateStatus("APPROVED"),
    });
  };

  const handleConfirmOrder = () => {
    setConfirmConfig({
      isOpen: true,
      title: "Kirim Purchase Order ke Supplier?",
      message:
        "Apakah Anda yakin ingin mengubah status PO ini menjadi Dikirim ke Supplier?",
      confirmText: "Kirim PO",
      type: "info",
      onConfirm: () => handleUpdateStatus("ORDERED"),
    });
  };

  const handleEditSubmit = async (payload, poId) => {
    try {
      setProcessing(true);
      await inventoryService.updatePurchaseOrder(poId || id, payload);
      showNotification("success", "Purchase Order berhasil diperbarui");
      setIsEditModalOpen(false);
      await fetchData();
    } catch (err) {
      console.error("Failed to update purchase order:", err);
      showNotification(
        "error",
        err.response?.data?.message ||
          err.message ||
          "Gagal memperbarui Purchase Order",
      );
    } finally {
      setProcessing(false);
    }
  };

  // Submit Receiving Intake
  const handleSubmitReceiving = async () => {
    try {
      setProcessing(true);
      const payloadItems = items
        .map((item) => {
          const qty = Number(receivedQuantities[item.id] || 0);
          const price = Number(
            actualPrices[item.id] ||
              item.unit_price_estimated ||
              item.unit_price ||
              0,
          );
          const batch = batchNumbers[item.id] || undefined;
          const condition = itemConditions[item.id] || "GOOD";
          return {
            po_item_id: item.id,
            product_id: item.product_id,
            variant_id: item.variant_id || undefined,
            quantity: qty,
            unit_price: price,
            batch_number: batch,
            condition: condition,
          };
        })
        .filter((item) => item.quantity > 0);

      if (payloadItems.length === 0) {
        showNotification(
          "error",
          "Harap masukkan minimal 1 barang dengan jumlah diterima lebih dari 0",
        );
        setProcessing(false);
        return;
      }

      const today = new Date().toISOString().split("T")[0];
      const payload = {
        po_id: id,
        received_date: receivedDate || today,
        notes: receivingNotes || undefined,
        attachments: attachments
          .map((a) => a.preview || a.url || (typeof a === "string" ? a : ""))
          .filter(Boolean),
        items: payloadItems,
      };

      // Auto-update master product purchase_price if valuation method is LAST_PURCHASE_PRICE or MOVING_AVERAGE
      const valuationMethod = localStorage.getItem("hpp_valuation_method") || "STANDARD";
      let preReceivingStocks = [];
      if (valuationMethod === "MOVING_AVERAGE") {
        try {
          const stockRes = await inventoryService.getStocks({ limit: 1000 });
          preReceivingStocks = stockRes?.data || [];
        } catch (e) {
          console.warn("Could not fetch pre-receiving stock levels:", e);
        }
      }

      await inventoryService.createReceiving(payload);

      if (valuationMethod === "LAST_PURCHASE_PRICE" || valuationMethod === "MOVING_AVERAGE") {
        for (const item of payloadItems) {
          try {
            if (!item.product_id || Number(item.unit_price) <= 0) continue;
            const prodRes = await masterService.products.get(item.product_id);
            const product = prodRes?.data || prodRes;
            if (!product) continue;

            const receivedQty = Number(item.quantity) || 0;
            const actualPrice = Number(item.unit_price) || 0;

            if (valuationMethod === "LAST_PURCHASE_PRICE") {
              if (item.variant_id && Array.isArray(product.variants) && product.variants.length > 0) {
                const variant = product.variants.find((v) => String(v.id) === String(item.variant_id));
                if (variant) {
                  await masterService.products.variants.update(item.product_id, item.variant_id, {
                    ...variant,
                    purchase_price: actualPrice,
                  });
                }
              } else {
                await masterService.products.update(item.product_id, {
                  ...product,
                  purchase_price: actualPrice,
                });
              }
            } else if (valuationMethod === "MOVING_AVERAGE") {
              // Find matching pre-receiving stock balance
              let matchedStock = null;
              if (item.variant_id) {
                matchedStock = preReceivingStocks.find(
                  (s) =>
                    (s.variant_id && String(s.variant_id) === String(item.variant_id)) ||
                    (s.variant && String(s.variant.id) === String(item.variant_id))
                );
              }
              if (!matchedStock) {
                matchedStock = preReceivingStocks.find(
                  (s) => String(s.product_id) === String(item.product_id) && !s.variant_id
                );
              }

              const currentStock = Number(matchedStock?.current_stock ?? 0);

              if (item.variant_id && Array.isArray(product.variants) && product.variants.length > 0) {
                const variant = product.variants.find((v) => String(v.id) === String(item.variant_id));
                if (variant) {
                  const varPrice = Number(variant.purchase_price ?? product.purchase_price ?? 0);
                  const varTotalUnits = Math.max(1, currentStock + receivedQty);
                  const varAvgPrice = Math.round((Math.max(0, currentStock * varPrice) + (receivedQty * actualPrice)) / varTotalUnits);
                  await masterService.products.variants.update(item.product_id, item.variant_id, {
                    ...variant,
                    purchase_price: varAvgPrice,
                  });
                }
              } else {
                const currentPrice = Number(product.purchase_price ?? 0);
                const totalUnits = Math.max(1, currentStock + receivedQty);
                const totalVal = Math.max(0, currentStock * currentPrice) + (receivedQty * actualPrice);
                const newAvgPrice = Math.round(totalVal / totalUnits);
                await masterService.products.update(item.product_id, {
                  ...product,
                  purchase_price: newAvgPrice,
                });
              }
            }
          } catch (e) {
            console.warn("Could not auto-update product valuation:", e);
          }
        }
      }

      showNotification(
        "success",
        "Barang berhasil diterima dan stok persediaan telah ditambahkan",
      );
      setIsReceiveModalOpen(false);
      fetchData();
    } catch (err) {
      console.error("Failed to receive goods:", err);
      showNotification(
        "error",
        err.response?.data?.message || "Gagal memproses penerimaan barang",
      );
    } finally {
      setProcessing(false);
    }
  };

  const handleCategoryFileUpload = (type, title, e) => {
    const files = Array.from(e.target.files || []);
    files.forEach((file) => {
      const reader = new FileReader();
      reader.onload = (event) => {
        setAttachments((prev) => [
          ...prev,
          {
            id: Math.random().toString(36).substring(2, 9),
            type,
            title,
            file,
            preview: event.target.result,
            name: file.name,
          },
        ]);
      };
      reader.readAsDataURL(file);
    });
    e.target.value = "";
  };

  const removeAttachment = (idToRemove) => {
    setAttachments((prev) => prev.filter((a) => a.id !== idToRemove));
  };

  if (loading) {
    return (
      <div className="flex h-screen bg-slate-50 items-center justify-center">
        <Loader className="w-8 h-8 animate-spin text-teal-600" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="flex h-screen bg-slate-50 items-center justify-center">
        <p className="text-slate-500 font-medium">Data Purchase Order tidak ditemukan</p>
      </div>
    );
  }

  const statusUpper = status?.toUpperCase();
  const isDraft = statusUpper === "DRAFT" || statusUpper === "SUBMITTED";
  const isApproved = statusUpper === "APPROVED";
  const isShippedOrPartial =
    statusUpper === "ORDERED" || statusUpper === "PARTIAL_RECEIVED";
  const isCompleted = statusUpper === "COMPLETED";
  const isCancelled =
    statusUpper === "CANCELLED" || statusUpper === "REJECTED";

  const subtotal = items.reduce((sum, item) => {
    const qty = Number(item.qty_ordered || item.quantity || 0);
    const price = Number(item.unit_price_estimated || item.unit_price || 0);
    return sum + (item.total_price_estimated || qty * price);
  }, 0);

  const totalAmount = data.total_estimated_amount ?? data.total_amount ?? subtotal;

  const publicNotes = (() => {
    if (!data?.notes) return "-";
    const cleaned = data.notes
      .replace(/\[PIC:[^\]]+\]/g, "")
      .replace(/\[Catatan Internal:[^\]]+\]/g, "")
      .split("|")
      .map((s) => s.trim())
      .filter(Boolean)
      .join(" | ");
    return cleaned || "-";
  })();

  const internalNotes = (() => {
    if (!data?.notes) return "-";
    const match = data.notes.match(/\[Catatan Internal:\s*([^\]]+)\]/);
    if (match) return match[1]?.trim() || "-";
    return "-";
  })();

  // Collect all photos from Receivings and PO
  const allProofPhotos = (() => {
    const list = [];
    (receivings || []).forEach((rcv) => {
      const rawAtts =
        rcv.attachments || (rcv.attachment_url ? [rcv.attachment_url] : []);
      const atts = Array.isArray(rawAtts) ? rawAtts : [];
      atts.forEach((att, idx) => {
        if (!att) return;
        const isObj = typeof att === "object" && att !== null;
        const url = isObj ? att.url : att;
        if (!url) return;

        let title = isObj ? att.title : null;
        let type = isObj ? att.type : null;

        if (!title) {
          if (idx === 0) {
            title = "Surat Jalan / Faktur";
            type = "SURAT_JALAN";
          } else if (idx === 1) {
            title = "Fisik Barang";
            type = "FISIK_BARANG";
          } else if (idx === 2) {
            title = "Bukti Serah Terima";
            type = "SERAH_TERIMA";
          } else {
            title = `Bukti Terima ${rcv.receiving_number || ""}`;
            type = "BUKTI_TERIMA";
          }
        }

        list.push({
          url,
          title,
          type: type || "BUKTI_TERIMA",
          date: rcv.received_date || rcv.created_at,
          receivedBy: rcv.received_by_name || "Staf Outlet",
          source: `Penerimaan ${rcv.receiving_number || ""}`,
        });
      });
    });

    const poAtts =
      data?.attachments || (data?.attachment_url ? [data.attachment_url] : []);
    const poList = Array.isArray(poAtts) ? poAtts : [];
    poList.forEach((att) => {
      const url = typeof att === "object" && att !== null ? att.url : att;
      if (url && !list.some((item) => item.url === url)) {
        list.push({
          url,
          title: "Lampiran Dokumen PO",
          type: "DOKUMEN_PO",
          date: data?.request_date || data?.created_at,
          receivedBy: data?.created_by_name || "Admin",
          source: `PO ${data?.po_number || ""}`,
        });
      }
    });
    return list;
  })();

  return (
    <div className="flex h-screen bg-slate-50">
      <Sidebar
        isCollapsed={isSidebarCollapsed}
        onToggle={() =>
          setIsSidebarCollapsed((prev) => {
            const newState = !prev;
            localStorage.setItem("sidebarCollapsed", JSON.stringify(newState));
            return newState;
          })
        }
        activePage="inventori"
      />

      <div
        className={`flex-1 overflow-auto transition-all duration-300 ${
          isSidebarCollapsed ? "ml-16" : "ml-48"
        }`}>
        <Header />

        <div className="p-8 max-w-7xl mx-auto space-y-6">
          {/* Top Header & Breadcrumbs */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
            <h1 className="text-xl font-bold text-slate-900 tracking-tight">
              Detail Purchase Order
            </h1>
            <div className="flex items-center gap-1.5 text-xs text-slate-500 font-medium">
              <Link to="/home" className="hover:text-teal-600 transition-colors">
                Home
              </Link>
              <span>&gt;</span>
              <Link
                to="/inventori/purchase-orders"
                className="hover:text-teal-600 transition-colors">
                Purchase Order
              </Link>
              <span>&gt;</span>
              <span className="text-slate-800 font-semibold">Detail</span>
            </div>
          </div>

          {/* Main Card Container */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 space-y-6">
            {/* Card Header */}
            <div className="flex items-start justify-between border-b border-slate-100 pb-4">
              <div>
                <div className="flex items-center gap-3">
                  <h2 className="text-lg font-bold text-slate-900">
                    Detail Purchase Order
                  </h2>
                  {getStatusBadge(status)}
                </div>
                <p className="text-xs text-slate-500 mt-1">
                  Informasi lengkap purchase order dan bahan yang dibeli
                </p>
              </div>
              <button
                type="button"
                onClick={() => navigate("/inventori/purchase-orders")}
                className="text-slate-400 hover:text-slate-600 transition-colors p-1.5 rounded-lg hover:bg-slate-100"
                title="Tutup">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* 8 Field Information Box Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
              {/* Nomor PO */}
              <div>
                <label className="block text-[11px] font-bold text-slate-600 mb-1">
                  Nomor PO
                </label>
                <div className="bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2.5 text-xs font-bold text-slate-900 truncate">
                  {data.po_number || "-"}
                </div>
              </div>

              {/* Supplier */}
              <div>
                <label className="block text-[11px] font-bold text-slate-600 mb-1">
                  Supplier
                </label>
                <div
                  className="bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2.5 text-xs font-medium text-slate-900 truncate"
                  title={data.supplier?.name || data.supplier_name || "-"}>
                  {data.supplier?.name || data.supplier_name || "-"}
                </div>
              </div>

              {/* Tanggal Order */}
              <div>
                <label className="block text-[11px] font-bold text-slate-600 mb-1">
                  Tanggal Order
                </label>
                <div className="bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2.5 text-xs font-medium text-slate-900">
                  {formatDate(data.request_date || data.created_at)}
                </div>
              </div>

              {/* Tanggal Estimasi */}
              <div>
                <label className="block text-[11px] font-bold text-slate-600 mb-1">
                  Tanggal Estimasi
                </label>
                <div className="bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2.5 text-xs font-medium text-slate-900">
                  {formatDate(data.due_date)}
                </div>
              </div>

              {/* Dibuat Oleh */}
              <div>
                <label className="block text-[11px] font-bold text-slate-600 mb-1">
                  Dibuat Oleh
                </label>
                <div className="bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2.5 text-xs font-medium text-slate-900 truncate">
                  {data.created_by_user?.full_name ||
                    data.created_by_user?.name ||
                    data.created_by_name ||
                    data.created_by ||
                    "Admin"}
                </div>
              </div>

              {/* PIC Supplier */}
              <div>
                <label className="block text-[11px] font-bold text-slate-600 mb-1">
                  PIC Supplier
                </label>
                <div className="bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2.5 text-xs font-medium text-slate-900 truncate">
                  {data.supplier_pic ||
                    data.pic_supplier ||
                    data.pic_name ||
                    data.supplier?.contact_name ||
                    data.supplier?.pic_name ||
                    (data.notes?.match(/\[PIC:\s*([^\]\-]+)/)?.[1]?.trim()) ||
                    "-"}
                </div>
              </div>

              {/* No. Telepon */}
              <div>
                <label className="block text-[11px] font-bold text-slate-600 mb-1">
                  No. Telepon
                </label>
                <div className="bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2.5 text-xs font-medium text-slate-900 truncate">
                  {data.supplier_phone ||
                    data.supplier?.phone_number ||
                    data.supplier?.phone ||
                    (data.notes?.match(/\[PIC:[^\]]*-\s*([^\]]+)\]/)?.[1]?.trim()) ||
                    "-"}
                </div>
              </div>

              {/* Catatan */}
              <div>
                <label className="block text-[11px] font-bold text-slate-600 mb-1">
                  Catatan
                </label>
                <div
                  className="bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2.5 text-xs font-medium text-slate-900 truncate"
                  title={publicNotes}>
                  {publicNotes}
                </div>
              </div>
            </div>

            {/* Items Table */}
            <div className="border border-slate-200 rounded-xl overflow-x-auto">
              <table className="w-full text-left">
                <thead className="bg-slate-50/80 border-b border-slate-200 text-[11px] font-bold text-slate-600 uppercase tracking-wider">
                  <tr>
                    <th className="px-4 py-3 text-center w-12">NO</th>
                    <th className="px-4 py-3">
                      {isRetail ? "NAMA BAHAN" : "NAMA BAHAN & PRODUK"}
                    </th>
                    {!isRetail && <th className="px-4 py-3 w-28">UNIT</th>}
                    {!isRetail && <th className="px-4 py-3 w-28">MEREK</th>}
                    <th className="px-4 py-3 text-center w-24">SATUAN</th>
                    <th className="px-4 py-3 text-center w-24">QTY</th>
                    <th className="px-4 py-3 text-right w-36">HARGA SATUAN</th>
                    <th className="px-4 py-3 text-right w-36">SUBTOTAL</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200 text-xs">
                  {items.length === 0 ? (
                    <tr>
                      <td
                        colSpan={isRetail ? 6 : 8}
                        className="px-4 py-8 text-center text-slate-400">
                        Tidak ada item dalam purchase order ini
                      </td>
                    </tr>
                  ) : (
                    items.map((item, idx) => {
                      const qty = Number(
                        item.qty_ordered || item.quantity || 0,
                      );
                      const unitPrice = Number(
                        item.unit_price_estimated || item.unit_price || 0,
                      );
                      const itemSubtotal =
                        item.total_price_estimated ?? qty * unitPrice;

                      return (
                        <tr
                          key={item.id || idx}
                          className="hover:bg-slate-50/70 transition-colors">
                          {/* NO */}
                          <td className="px-4 py-3 text-center text-slate-500 font-medium">
                            {idx + 1}
                          </td>

                          {/* NAMA BAHAN */}
                          <td className="px-4 py-3 text-slate-900 font-semibold">
                            <div>{item.product_name || item.name || "-"}</div>
                            {item.variant_name && item.variant_name !== (item.brand_name || item.brand || item.variant_name) && (
                              <div className="text-[11px] font-normal text-slate-500">
                                Varian: {item.variant_name}
                              </div>
                            )}
                          </td>

                          {/* Legiun custom UNIT */}
                          {!isRetail && (
                            <td className="px-4 py-3 text-slate-700 font-medium text-teal-700">
                              {item.unit_name || item.asset_unit_name || item.unit || "-"}
                            </td>
                          )}

                          {/* Legiun custom MEREK */}
                          {!isRetail && (
                            <td className="px-4 py-3 text-slate-700 font-medium">
                              {item.brand_name || item.brand || item.variant_name || "-"}
                            </td>
                          )}

                          {/* SATUAN */}
                          <td className="px-4 py-3 text-center text-slate-700">
                            {item.product_unit_name ||
                              item.satuan_name ||
                              item.satuan ||
                              "Pcs"}
                          </td>

                          {/* QTY */}
                          <td className="px-4 py-3 text-center font-bold text-slate-900">
                            {formatNumberWithSeparator(qty)}
                          </td>

                          {/* HARGA SATUAN */}
                          <td className="px-4 py-3 text-right text-slate-800 font-medium">
                            {formatCurrency(unitPrice)}
                          </td>

                          {/* SUBTOTAL */}
                          <td className="px-4 py-3 text-right font-bold text-slate-900">
                            {formatCurrency(itemSubtotal)}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {/* Lower Section: Catatan Internal & Ringkasan Belanja */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-2">
              {/* Left: Catatan Internal */}
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-2">
                  Catatan Internal
                </label>
                <div className="bg-slate-50 border border-slate-200 rounded-2xl p-4 min-h-[120px] text-xs text-slate-700 whitespace-pre-wrap leading-relaxed">
                  {internalNotes}
                </div>
              </div>

              {/* Right: Ringkasan Belanja */}
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-2">
                  Ringkasan Belanja
                </label>
                <div className="space-y-2.5 text-xs">
                  <div className="flex justify-between items-center text-slate-600">
                    <span>Subtotal</span>
                    <span className="font-semibold text-slate-900">
                      {formatCurrency(subtotal)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center text-slate-600">
                    <span>Diskon</span>
                    <span className="font-semibold text-slate-900">
                      {formatCurrency(data.discount_amount || 0)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center text-slate-600">
                    <span>Biaya Tambahan</span>
                    <span className="font-semibold text-slate-900">
                      {formatCurrency(data.additional_fee || 0)}
                    </span>
                  </div>
                  <div className="border-t border-slate-200 pt-3 flex justify-between items-baseline">
                    <span className="font-bold text-sm text-slate-900">
                      Total Belanja
                    </span>
                    <span className="font-extrabold text-lg text-slate-900">
                      {formatCurrency(totalAmount)}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Riwayat Penerimaan Barang (Goods Receipt) */}
            {receivings.length > 0 && (
              <div className="border-t border-slate-100 pt-6 space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-xs font-bold text-slate-800 uppercase tracking-wider flex items-center gap-2">
                    <PackageCheck className="w-4 h-4 text-teal-600" />
                    <span>Riwayat Penerimaan Barang (Faktur & Realisasi)</span>
                    <span className="bg-teal-50 text-teal-700 text-[10px] px-2 py-0.5 rounded-full font-semibold ring-1 ring-teal-200">
                      {receivings.length} Penerimaan
                    </span>
                  </h3>
                </div>

                <div className="space-y-4">
                  {receivings.map((rcv, rcvIdx) => {
                    const totalRcvAmount = (rcv.items || []).reduce(
                      (sum, it) => sum + Number(it.total_price_realized || (it.qty_received * it.unit_price_realized) || 0),
                      0,
                    );
                    const totalRcvQty = (rcv.items || []).reduce(
                      (sum, it) => sum + Number(it.qty_received || 0),
                      0,
                    );

                    return (
                      <div
                        key={rcv.id || rcvIdx}
                        className="bg-slate-50/80 border border-slate-200 rounded-2xl p-4 space-y-3 shadow-2xs">
                        {/* Receiving Header */}
                        <div className="flex flex-wrap items-center justify-between gap-2 pb-2.5 border-b border-slate-200 text-xs">
                          <div className="flex items-center gap-2.5">
                            <span className="font-extrabold text-slate-900 text-sm tracking-tight">
                              {rcv.receiving_number || `Penerimaan #${rcvIdx + 1}`}
                            </span>
                            <span className="bg-white ring-1 ring-slate-200 text-slate-700 text-[11px] px-2.5 py-0.5 rounded-md font-semibold">
                              {formatDate(rcv.received_date || rcv.created_at)}
                            </span>
                          </div>
                          <div className="flex items-center gap-3">
                            <div className="text-slate-600 text-xs flex items-center gap-1.5">
                              <span>Diterima Oleh</span>
                              <span className="font-bold text-slate-900 bg-white px-2 py-0.5 rounded-md ring-1 ring-slate-200">
                                {rcv.received_by_name || "Staf Outlet"}
                              </span>
                            </div>
                            <button
                              type="button"
                              onClick={() => navigate(`/inventori/purchase-orders/${id}/receiving/${rcv.id}/print`)}
                              className="inline-flex items-center gap-1.5 px-3 py-1 bg-white border border-teal-300 text-teal-700 hover:bg-teal-50 rounded-lg text-xs font-bold shadow-2xs transition">
                              <Printer className="w-3.5 h-3.5 text-teal-600" />
                              <span>Cetak Bukti Penerimaan</span>
                            </button>
                          </div>
                        </div>

                        {/* Items Received Table */}
                        <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
                          <table className="w-full text-left">
                            <thead className="text-[10px] font-bold text-slate-500 uppercase tracking-wider bg-slate-50 border-b border-slate-200">
                              <tr>
                                <th className="py-2.5 px-3 text-center w-10">NO</th>
                                <th className="py-2.5 px-3">PRODUK & VARIAN</th>
                                {!isRetail && <th className="py-2.5 px-3 w-36">TARGET UNIT</th>}
                                {!isRetail && <th className="py-2.5 px-3 w-28">MEREK</th>}
                                <th className="py-2.5 px-3 text-center w-20">SATUAN</th>
                                <th className="py-2.5 px-3 text-center w-24">QTY DITERIMA</th>
                                <th className="py-2.5 px-3 text-right w-36">HARGA REALISASI</th>
                                <th className="py-2.5 px-3 text-right w-36">SUBTOTAL</th>
                                <th className="py-2.5 px-3 text-center w-20">KONDISI</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100 text-xs">
                              {(rcv.items || []).map((rItem, iIdx) => {
                                const matchedPO = (data?.items || []).find(
                                  (p) => p.id === rItem.po_item_id || p.product_id === rItem.product_id,
                                );
                                const targetUnit = rItem.unit_name || matchedPO?.unit_name || matchedPO?.asset_unit_name || "-";
                                const brandName = rItem.brand_name || matchedPO?.brand_name || rItem.variant_name || matchedPO?.variant_name || "-";
                                const satuan = rItem.product_unit_name || matchedPO?.product_unit_name || matchedPO?.satuan_name || "Pcs";
                                const rQty = Number(rItem.qty_received || 0);
                                const rPrice = Number(rItem.unit_price_realized || 0);
                                const rTotal = Number(rItem.total_price_realized || rQty * rPrice);
                                const conditionLabel =
                                  rItem.condition === "GOOD"
                                    ? "Baik"
                                    : rItem.condition === "DAMAGED"
                                    ? "Rusak"
                                    : rItem.condition === "EXPIRED"
                                    ? "Kedaluwarsa"
                                    : rItem.condition || "Baik";

                                return (
                                  <tr key={rItem.id || iIdx} className="hover:bg-slate-50/80 transition-colors">
                                    <td className="py-2.5 px-3 text-center text-slate-500 font-medium">
                                      {iIdx + 1}
                                    </td>
                                    <td className="py-2.5 px-3 text-slate-900 font-semibold">
                                      <div>{rItem.product_name || "-"}</div>
                                      {rItem.variant_name && rItem.variant_name !== brandName && (
                                        <div className="text-[11px] font-normal text-slate-500">
                                          Varian: {rItem.variant_name}
                                        </div>
                                      )}
                                    </td>
                                    {!isRetail && (
                                      <td className="py-2.5 px-3 text-slate-700 font-medium text-teal-700">
                                        {targetUnit}
                                      </td>
                                    )}
                                    {!isRetail && (
                                      <td className="py-2.5 px-3 text-slate-700">
                                        {brandName}
                                      </td>
                                    )}
                                    <td className="py-2.5 px-3 text-center text-slate-700">
                                      {satuan}
                                    </td>
                                    <td className="py-2.5 px-3 text-center font-bold text-slate-900">
                                      {formatNumberWithSeparator(rQty)}
                                    </td>
                                    <td className="py-2.5 px-3 text-right font-extrabold text-teal-700">
                                      {formatCurrency(rPrice)}
                                    </td>
                                    <td className="py-2.5 px-3 text-right font-bold text-slate-900">
                                      {formatCurrency(rTotal)}
                                    </td>
                                    <td className="py-2.5 px-3 text-center">
                                      <span
                                        className={`px-2.5 py-0.5 rounded-full text-[10px] font-semibold ${
                                          rItem.condition === "GOOD" || !rItem.condition
                                            ? "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200"
                                            : "bg-rose-50 text-rose-700 ring-1 ring-rose-200"
                                        }`}>
                                        {conditionLabel}
                                      </span>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>

                        {/* Lower Receiving Summary: Total Belanja Realisasi & Notes */}
                        <div className="flex flex-wrap items-center justify-between gap-3 pt-1 border-t border-slate-200 text-xs">
                          <div className="text-slate-500 font-medium">
                            <span>Total Item: {(rcv.items || []).length} Item</span>
                            <span className="mx-2">•</span>
                            <span>Total Fisik Diterima: {formatNumberWithSeparator(totalRcvQty)}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-slate-600">Total Nilai Faktur Realisasi:</span>
                            <span className="font-extrabold text-sm text-teal-700 bg-teal-50 border border-teal-200 px-3 py-1 rounded-lg shadow-2xs">
                              {formatCurrency(totalRcvAmount)}
                            </span>
                          </div>
                        </div>

                        {/* Notes if any */}
                        {rcv.notes && (
                          <div className="text-xs text-slate-700 bg-white p-3 rounded-xl border border-slate-200 leading-relaxed">
                            <span className="font-bold text-slate-800">Catatan Penerimaan: </span>
                            <span className="text-slate-600">{rcv.notes}</span>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Lampiran Foto Surat Jalan & Bukti Penerimaan */}
            {allProofPhotos.length > 0 && (
              <div className="border-t border-slate-100 pt-6 space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="text-xs font-bold text-slate-800 uppercase tracking-wider flex items-center gap-2">
                    <ImageIcon className="w-4 h-4 text-teal-600" />
                    <span>Foto Surat Jalan & Bukti Penerimaan Barang</span>
                    <span className="bg-teal-50 text-teal-700 text-[10px] px-2 py-0.5 rounded-full font-semibold ring-1 ring-teal-200">
                      {allProofPhotos.length} Foto
                    </span>
                  </h3>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3.5">
                  {allProofPhotos.map((photo, i) => {
                    const badgeCls =
                      photo.type === "SURAT_JALAN"
                        ? "bg-sky-50 text-sky-700 ring-sky-200"
                        : photo.type === "FISIK_BARANG"
                        ? "bg-emerald-50 text-emerald-700 ring-emerald-200"
                        : photo.type === "SERAH_TERIMA"
                        ? "bg-purple-50 text-purple-700 ring-purple-200"
                        : "bg-slate-100 text-slate-700 ring-slate-200";

                    return (
                      <div
                        key={i}
                        onClick={() => setPreviewImageUrl(photo.url)}
                        className="group cursor-pointer bg-slate-50 hover:bg-slate-100/80 border border-slate-200 rounded-xl p-2 transition shadow-2xs hover:shadow-md flex flex-col justify-between">
                        <div>
                          <div className="relative aspect-4/3 w-full rounded-lg overflow-hidden bg-slate-200">
                            <img
                              src={photo.url}
                              alt={photo.title}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                            />
                            <div className="absolute inset-0 bg-slate-950/20 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                              <Eye className="w-5 h-5 text-white drop-shadow" />
                            </div>
                          </div>
                          <div className="mt-2 min-w-0">
                            <span
                              className={`inline-block text-[9px] font-bold px-1.5 py-0.5 rounded ring-1 mb-1 truncate max-w-full ${badgeCls}`}>
                              {photo.title}
                            </span>
                            <p className="text-[10px] text-slate-500">
                              {formatDate(photo.date)}
                            </p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Bottom Actions Bar */}
            <div className="border-t border-slate-100 pt-5 flex flex-col sm:flex-row items-center justify-between gap-3">
              {/* Left: Batalkan PO */}
              <div>
                {!isCompleted && !isCancelled && (
                  <button
                    type="button"
                    onClick={handleConfirmCancel}
                    disabled={processing}
                    className="px-4 py-2.5 rounded-xl border border-rose-500 text-rose-600 hover:bg-rose-50 font-bold text-xs transition shadow-sm flex items-center gap-2">
                    <Trash2 className="w-4 h-4 text-rose-600" />
                    <span>Batalkan PO</span>
                  </button>
                )}
              </div>

              {/* Right: Status-specific Actions */}
              <div className="flex items-center gap-3">
                {/* Cetak PO - Always Available */}
                <button
                  type="button"
                  onClick={() =>
                    navigate(`/inventori/purchase-orders/${id}/print`)
                  }
                  className="px-4 py-2.5 rounded-xl border border-indigo-500 text-indigo-600 hover:bg-indigo-50 font-bold text-xs transition shadow-sm flex items-center gap-2">
                  <Printer className="w-4 h-4 text-indigo-600" />
                  <span>Cetak PO</span>
                </button>

                {/* Status: DRAFT / SUBMITTED -> Edit PO & Setujui PO */}
                {isDraft && (
                  <>
                    <button
                      type="button"
                      onClick={() => setIsEditModalOpen(true)}
                      className="px-4 py-2.5 rounded-xl border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 font-bold text-xs transition shadow-sm flex items-center gap-2">
                      <Edit className="w-4 h-4 text-slate-600" />
                      <span>Edit PO</span>
                    </button>

                    <button
                      type="button"
                      onClick={handleConfirmApprove}
                      disabled={processing}
                      className="px-5 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs transition shadow-sm shadow-emerald-600/20 flex items-center gap-2">
                      <Check className="w-4 h-4" />
                      <span>Setujui PO</span>
                    </button>
                  </>
                )}

                {/* Status: APPROVED -> Kirim PO */}
                {isApproved && (
                  <button
                    type="button"
                    onClick={handleConfirmOrder}
                    disabled={processing}
                    className="px-5 py-2.5 rounded-xl bg-sky-600 hover:bg-sky-700 text-white font-bold text-xs transition shadow-sm shadow-sky-600/20 flex items-center gap-2">
                    <ArrowRight className="w-4 h-4" />
                    <span>Kirim PO</span>
                  </button>
                )}

                {/* Status: ORDERED / PARTIAL_RECEIVED -> Terima Barang */}
                {isShippedOrPartial && (
                  <button
                    type="button"
                    onClick={() => setIsReceiveModalOpen(true)}
                    disabled={processing}
                    className="px-5 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-xs transition shadow-sm shadow-indigo-600/20 flex items-center gap-2">
                    <PackageCheck className="w-4 h-4" />
                    <span>Terima Barang</span>
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={confirmConfig.isOpen}
        onClose={() => setConfirmConfig({ ...confirmConfig, isOpen: false })}
        onConfirm={confirmConfig.onConfirm}
        title={confirmConfig.title}
        message={confirmConfig.message}
        confirmText={processing ? "Memproses..." : confirmConfig.confirmText}
        type={confirmConfig.type}
        disabled={processing}
      />

      {/* Receiving Intake Modal */}
      <Modal
        isOpen={isReceiveModalOpen}
        onClose={() => {
          if (!processing) setIsReceiveModalOpen(false);
        }}
        title="Terima Barang Purchase Order"
        maxWidth="max-w-4xl">
        <div className="space-y-6">
          {/* Top Form Fields: Tanggal Terima * & Catatan */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 bg-slate-50/70 p-4 rounded-2xl border border-slate-200/80">
            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1">
                Tanggal Terima <span className="text-rose-500">*</span>
              </label>
              <input
                type="date"
                value={receivedDate}
                onChange={(e) => setReceivedDate(e.target.value)}
                className="w-full px-3.5 py-2.5 bg-white border border-slate-300 rounded-xl text-xs text-slate-800 focus:ring-2 focus:ring-teal-500 focus:border-teal-500 focus:outline-none shadow-2xs font-medium"
              />
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1">
                Catatan Penerimaan
              </label>
              <input
                type="text"
                value={receivingNotes}
                onChange={(e) => setReceivingNotes(e.target.value)}
                placeholder="Catatan kondisi penerimaan / ekspedisi (opsional)"
                className="w-full px-3.5 py-2.5 bg-white border border-slate-300 rounded-xl text-xs text-slate-800 placeholder-slate-400 focus:ring-2 focus:ring-teal-500 focus:border-teal-500 focus:outline-none shadow-2xs font-medium"
              />
            </div>
          </div>

          {/* Section: Item yang Diterima */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-bold text-slate-800">
                Item yang Diterima
              </h4>
              <span className="text-xs text-slate-500 font-medium">
                {items.length} jenis item
              </span>
            </div>

            <div className="space-y-3">
              {items.map((item) => {
                const qtyOrdered = Number(item.qty_ordered || item.quantity || 0);
                const alreadyReceived = Number(item.qty_received || 0);
                const remaining = Math.max(0, qtyOrdered - alreadyReceived);
                const maxAllowed = remaining > 0 ? remaining : qtyOrdered;
                const physicalSatuan = item.product_unit_name || item.satuan_name || item.satuan || "Pcs";
                const targetUnitMesin = item.unit_name || item.asset_unit_name || "";
                const brandDisplay = item.brand_name || item.brand || item.variant_name || "";
                const isVariantDistinctFromBrand = item.variant_name && item.variant_name !== brandDisplay;

                return (
                  <div
                    key={item.id}
                    className="p-4 bg-slate-50/90 border border-slate-200/90 rounded-2xl space-y-3 shadow-2xs">
                    {/* Top Row: Product Title & Variant Badge & Order Status */}
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-slate-200/70 pb-2.5">
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <h5 className="text-sm font-bold text-slate-900 leading-snug">
                            {item.product_name || item.name}
                          </h5>
                          {brandDisplay && (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-semibold bg-slate-100 text-slate-700 ring-1 ring-slate-200">
                              Merek: {brandDisplay}
                            </span>
                          )}
                          {isVariantDistinctFromBrand && (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-semibold bg-teal-50 text-teal-700 ring-1 ring-teal-200">
                              Varian: {item.variant_name}
                            </span>
                          )}
                          {targetUnitMesin && targetUnitMesin !== "-" && (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-semibold bg-teal-50 text-teal-800 ring-1 ring-teal-200">
                              Unit Mesin: {targetUnitMesin}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex flex-wrap items-center gap-1.5 shrink-0 text-xs">
                        <span className="inline-flex items-center px-2.5 py-1 rounded-lg bg-white border border-slate-200 text-slate-600 font-medium shadow-2xs">
                          Dipesan: <strong className="text-slate-900 ml-1">{qtyOrdered} {physicalSatuan}</strong>
                        </span>
                        {alreadyReceived > 0 && (
                          <span className="inline-flex items-center px-2.5 py-1 rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-700 font-medium">
                            Sudah Masuk: <strong className="ml-1">{alreadyReceived} {physicalSatuan}</strong>
                          </span>
                        )}
                        {alreadyReceived > 0 && (
                          <span className="inline-flex items-center px-2.5 py-1 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 font-semibold">
                            Sisa Belum Datang: <strong className="ml-1">{remaining} {physicalSatuan}</strong>
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Bottom: 4 Column Inputs Grid */}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-1">
                      <div>
                        <label className="block text-[11px] font-bold text-slate-700 mb-1">
                          Qty Diterima <span className="text-rose-500">*</span>
                        </label>
                        <input
                          type="text"
                          inputMode="numeric"
                          value={formatNumberWithSeparator(receivedQuantities[item.id] ?? "")}
                          onChange={(e) => {
                            const raw = parseNumberFromSeparator(e.target.value);
                            const val = Math.min(raw, maxAllowed);
                            setReceivedQuantities({
                              ...receivedQuantities,
                              [item.id]: val,
                            });
                          }}
                          className="w-full px-3 py-2 text-center font-bold bg-white border border-slate-300 rounded-xl text-xs text-slate-900 focus:ring-2 focus:ring-teal-500 focus:border-teal-500 focus:outline-none shadow-2xs"
                        />
                      </div>

                      <div>
                        <label className="block text-[11px] font-bold text-slate-700 mb-1">
                          Harga Satuan Realisasi <span className="text-rose-500">*</span>
                        </label>
                        <input
                          type="text"
                          inputMode="numeric"
                          value={formatNumberWithSeparator(actualPrices[item.id] ?? "")}
                          onChange={(e) => {
                            const raw = parseNumberFromSeparator(e.target.value);
                            setActualPrices({
                              ...actualPrices,
                              [item.id]: raw,
                            });
                          }}
                          className="w-full px-3 py-2 text-right font-bold bg-white border border-slate-300 rounded-xl text-xs text-slate-900 focus:ring-2 focus:ring-teal-500 focus:border-teal-500 focus:outline-none shadow-2xs"
                        />
                      </div>

                      <div>
                        <label className="block text-[11px] font-bold text-slate-700 mb-1">
                          Kondisi Fisik
                        </label>
                        <select
                          value={itemConditions[item.id] || "GOOD"}
                          onChange={(e) =>
                            setItemConditions({
                              ...itemConditions,
                              [item.id]: e.target.value,
                            })
                          }
                          className="w-full px-3 py-2 bg-white border border-slate-300 rounded-xl text-xs font-semibold text-slate-800 focus:ring-2 focus:ring-teal-500 focus:border-teal-500 focus:outline-none shadow-2xs">
                          <option value="GOOD">Baik</option>
                          <option value="DAMAGED">Rusak</option>
                          <option value="EXPIRED">Kedaluwarsa</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-[11px] font-bold text-slate-700 mb-1">
                          No. Batch
                        </label>
                        <input
                          type="text"
                          value={batchNumbers[item.id] || ""}
                          onChange={(e) =>
                            setBatchNumbers({
                              ...batchNumbers,
                              [item.id]: e.target.value,
                            })
                          }
                          placeholder="Auto"
                          className="w-full px-3 py-2 bg-white border border-slate-300 rounded-xl text-xs placeholder-slate-400 focus:ring-2 focus:ring-teal-500 focus:border-teal-500 focus:outline-none shadow-2xs font-medium"
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* 3 Kategori Foto Dokumentasi Penerimaan */}
          <div className="pt-3 border-t border-slate-200/80 space-y-3">
            <div>
              <label className="block text-xs font-bold text-slate-800 mb-0.5">
                Foto Dokumentasi Penerimaan Barang (Opsional)
              </label>
              <p className="text-[11px] text-slate-500">
                Unggah foto sesuai kategori untuk mempermudah audit fisik dan arsip faktur
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {/* Kategori 1: Surat Jalan / Faktur */}
              <div className="bg-slate-50 border border-slate-200 rounded-2xl p-3 flex flex-col justify-between space-y-2">
                <div>
                  <div className="flex items-center justify-between gap-1 mb-1">
                    <span className="text-xs font-bold text-slate-800">1. Surat Jalan / Faktur</span>
                    <span className="text-[10px] bg-sky-50 text-sky-700 font-semibold px-2 py-0.5 rounded-full ring-1 ring-sky-200">
                      {attachments.filter((a) => a.type === "SURAT_JALAN").length} Foto
                    </span>
                  </div>
                  <p className="text-[10px] text-slate-500">Nota fisik / invoice supplier</p>
                </div>

                <label className="cursor-pointer w-full py-2 bg-white hover:bg-sky-50 border border-dashed border-sky-300 rounded-xl text-xs font-semibold text-sky-700 flex items-center justify-center gap-1.5 transition">
                  <Upload className="w-3.5 h-3.5 text-sky-600" />
                  <span>Upload Surat Jalan</span>
                  <input
                    type="file"
                    multiple
                    accept="image/*"
                    onChange={(e) => handleCategoryFileUpload("SURAT_JALAN", "Surat Jalan / Faktur", e)}
                    className="hidden"
                  />
                </label>

                {attachments.filter((a) => a.type === "SURAT_JALAN").length > 0 && (
                  <div className="grid grid-cols-2 gap-2 pt-1">
                    {attachments
                      .filter((a) => a.type === "SURAT_JALAN")
                      .map((att) => (
                        <div
                          key={att.id}
                          className="relative group border border-slate-200 rounded-lg overflow-hidden bg-white aspect-4/3">
                          <img
                            src={att.preview}
                            alt={att.name}
                            className="w-full h-full object-cover"
                          />
                          <button
                            type="button"
                            onClick={() => removeAttachment(att.id)}
                            className="absolute top-1 right-1 bg-rose-600/90 hover:bg-rose-600 text-white rounded-full p-1 shadow">
                            <X className="w-2.5 h-2.5" />
                          </button>
                        </div>
                      ))}
                  </div>
                )}
              </div>

              {/* Kategori 2: Fisik Barang */}
              <div className="bg-slate-50 border border-slate-200 rounded-2xl p-3 flex flex-col justify-between space-y-2">
                <div>
                  <div className="flex items-center justify-between gap-1 mb-1">
                    <span className="text-xs font-bold text-slate-800">2. Fisik Barang</span>
                    <span className="text-[10px] bg-emerald-50 text-emerald-700 font-semibold px-2 py-0.5 rounded-full ring-1 ring-emerald-200">
                      {attachments.filter((a) => a.type === "FISIK_BARANG").length} Foto
                    </span>
                  </div>
                  <p className="text-[10px] text-slate-500">Kondisi bongkar muat barang</p>
                </div>

                <label className="cursor-pointer w-full py-2 bg-white hover:bg-emerald-50 border border-dashed border-emerald-300 rounded-xl text-xs font-semibold text-emerald-700 flex items-center justify-center gap-1.5 transition">
                  <Upload className="w-3.5 h-3.5 text-emerald-600" />
                  <span>Upload Fisik Barang</span>
                  <input
                    type="file"
                    multiple
                    accept="image/*"
                    onChange={(e) => handleCategoryFileUpload("FISIK_BARANG", "Fisik Barang", e)}
                    className="hidden"
                  />
                </label>

                {attachments.filter((a) => a.type === "FISIK_BARANG").length > 0 && (
                  <div className="grid grid-cols-2 gap-2 pt-1">
                    {attachments
                      .filter((a) => a.type === "FISIK_BARANG")
                      .map((att) => (
                        <div
                          key={att.id}
                          className="relative group border border-slate-200 rounded-lg overflow-hidden bg-white aspect-4/3">
                          <img
                            src={att.preview}
                            alt={att.name}
                            className="w-full h-full object-cover"
                          />
                          <button
                            type="button"
                            onClick={() => removeAttachment(att.id)}
                            className="absolute top-1 right-1 bg-rose-600/90 hover:bg-rose-600 text-white rounded-full p-1 shadow">
                            <X className="w-2.5 h-2.5" />
                          </button>
                        </div>
                      ))}
                  </div>
                )}
              </div>

              {/* Kategori 3: Bukti Serah Terima */}
              <div className="bg-slate-50 border border-slate-200 rounded-2xl p-3 flex flex-col justify-between space-y-2">
                <div>
                  <div className="flex items-center justify-between gap-1 mb-1">
                    <span className="text-xs font-bold text-slate-800">3. Serah Terima / TTD</span>
                    <span className="text-[10px] bg-purple-50 text-purple-700 font-semibold px-2 py-0.5 rounded-full ring-1 ring-purple-200">
                      {attachments.filter((a) => a.type === "SERAH_TERIMA").length} Foto
                    </span>
                  </div>
                  <p className="text-[10px] text-slate-500">Bukti TTD staf & kurir</p>
                </div>

                <label className="cursor-pointer w-full py-2 bg-white hover:bg-purple-50 border border-dashed border-purple-300 rounded-xl text-xs font-semibold text-purple-700 flex items-center justify-center gap-1.5 transition">
                  <Upload className="w-3.5 h-3.5 text-purple-600" />
                  <span>Upload Bukti TTD</span>
                  <input
                    type="file"
                    multiple
                    accept="image/*"
                    onChange={(e) => handleCategoryFileUpload("SERAH_TERIMA", "Bukti Serah Terima", e)}
                    className="hidden"
                  />
                </label>

                {attachments.filter((a) => a.type === "SERAH_TERIMA").length > 0 && (
                  <div className="grid grid-cols-2 gap-2 pt-1">
                    {attachments
                      .filter((a) => a.type === "SERAH_TERIMA")
                      .map((att) => (
                        <div
                          key={att.id}
                          className="relative group border border-slate-200 rounded-lg overflow-hidden bg-white aspect-4/3">
                          <img
                            src={att.preview}
                            alt={att.name}
                            className="w-full h-full object-cover"
                          />
                          <button
                            type="button"
                            onClick={() => removeAttachment(att.id)}
                            className="absolute top-1 right-1 bg-rose-600/90 hover:bg-rose-600 text-white rounded-full p-1 shadow">
                            <X className="w-2.5 h-2.5" />
                          </button>
                        </div>
                      ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Modal Footer Buttons */}
          <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-100">
            <button
              type="button"
              onClick={() => setIsReceiveModalOpen(false)}
              disabled={processing}
              className="px-5 py-2.5 rounded-xl border border-slate-200 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-xs transition">
              Batal
            </button>

            <button
              type="button"
              onClick={handleSubmitReceiving}
              disabled={processing}
              className="px-6 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-xs transition shadow-md shadow-indigo-600/20 flex items-center gap-2">
              {processing ? (
                <>
                  <Loader className="w-4 h-4 animate-spin" />
                  <span>Menyimpan...</span>
                </>
              ) : (
                <>
                  <CheckCircle2 className="w-4 h-4" />
                  <span>Simpan Penerimaan</span>
                </>
              )}
            </button>
          </div>
        </div>
      </Modal>

      {/* Edit Purchase Order Modal */}
      <AddProcurementModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        onSubmit={handleEditSubmit}
        editData={data}
      />

      {/* Image Lightbox Preview Modal */}
      {previewImageUrl && (
        <div
          onClick={() => setPreviewImageUrl(null)}
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/80 backdrop-blur-sm p-4 animate-in fade-in duration-200">
          <div
            onClick={(e) => e.stopPropagation()}
            className="relative max-w-4xl max-h-[90vh] bg-white rounded-2xl overflow-hidden shadow-2xl flex flex-col">
            <div className="flex items-center justify-between px-4 py-3 bg-slate-900 text-white">
              <span className="text-xs font-bold flex items-center gap-2">
                <ImageIcon className="w-4 h-4 text-teal-400" />
                <span>Foto Bukti Penerimaan Barang</span>
              </span>
              <button
                type="button"
                onClick={() => setPreviewImageUrl(null)}
                className="text-slate-400 hover:text-white transition p-1 rounded-lg hover:bg-slate-800">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-3 bg-slate-950 flex items-center justify-center overflow-auto max-h-[80vh]">
              <img
                src={previewImageUrl}
                alt="Bukti Penerimaan"
                className="max-w-full max-h-[75vh] object-contain rounded-lg shadow-lg"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
